import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery
} from 'react-query';
import { associationsService } from 'api/services/associations';
import { ErrorResponse } from 'api/types/errors';
import { AssociationGroupsQueryKey } from 'api/types/associations';
import {
  GetAssociationGroupsParams,
  AssociationGroupsResponse,
  AssociationGroupsSummaryResponse
} from 'api/dtos/associations';

export const associationGroupsKeys = {
  all: ['associationGroups'] as const,
  lists: () => [...associationGroupsKeys.all, 'list'] as const,
  list: (params: AssociationGroupsQueryKey) =>
    [...associationGroupsKeys.lists(), params] as const,
  infinite: (params: AssociationGroupsQueryKey) =>
    [...associationGroupsKeys.list(params), 'infinite'] as const,
  summary: (associationId: string) =>
    [...associationGroupsKeys.all, 'summary', associationId] as const
};

export const useAssociationGroupsInfinite = (
  params: Omit<GetAssociationGroupsParams, 'cursor'> & {
    enabled?: boolean;
    useUnauthenticatedEndpoint?: boolean;
  },
  options?: Omit<
    UseInfiniteQueryOptions<AssociationGroupsResponse, ErrorResponse>,
    'queryKey' | 'queryFn' | 'getNextPageParam'
  >
) => {
  const {
    enabled = true,
    useUnauthenticatedEndpoint = false,
    ...queryParams
  } = params;

  const queryKey = [
    ...associationGroupsKeys.infinite({
      associationId: params.associationId,
      query: params.query,
      letter: params.letter,
      limit: params.limit
    }),
    { useUnauthenticatedEndpoint }
  ];

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      associationsService.getAssociationGroups({
        ...queryParams,
        cursor: pageParam,
        useUnauthenticatedEndpoint
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    enabled: enabled && !!params.associationId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options
  });
};

export function useExportAssociationMembersCSV({
  associationId,
  enabled = false
}: {
  associationId: string;
  enabled?: boolean;
}) {
  return useQuery<Blob, ErrorResponse>(
    ['exportAssociationMembersCSV', { associationId }],
    () => associationsService.exportAssociationMembersCSV(associationId),
    {
      enabled,
      retry: false,
      refetchOnWindowFocus: false
    }
  );
}

export function useExportAssociationAffiliatesCSV({
  associationId,
  enabled = false
}: {
  associationId: string;
  enabled?: boolean;
}) {
  return useQuery<Blob, ErrorResponse>(
    ['exportAssociationAffiliatesCSV', { associationId }],
    () => associationsService.exportAssociationAffiliatesCSV(associationId),
    {
      enabled,
      retry: false,
      refetchOnWindowFocus: false
    }
  );
}

/**
 * Hook to fetch a lightweight summary of all organizations in an association.
 * Used for the organization allowlist selector in admin settings.
 */
export function useAssociationGroupsSummary({
  associationId,
  enabled = true
}: {
  associationId: string;
  enabled?: boolean;
}) {
  return useQuery<AssociationGroupsSummaryResponse, ErrorResponse>(
    associationGroupsKeys.summary(associationId),
    () => associationsService.getAssociationGroupsSummary(associationId),
    {
      enabled: enabled && !!associationId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000
    }
  );
}
